import Color from "color";
import React, { useState } from "react";

import Loader from "./loader";
import UTIcon, { UTIconProps } from "./icons";
import { usePartnerContext } from "../context/partner";
import { PartnerColors } from "../context/partner/types";

type Props = {
  children?: React.ReactNode;
  onClick?: (e: any) => void;
  type?: "button" | "submit" | "reset";
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  padding?: string;
  variant?: string;
  style?: any;
  color?: keyof PartnerColors;
  textColor?: keyof PartnerColors;
  icon?: UTIconProps["type"];
  iconColor?: keyof PartnerColors;
  align?: "left" | "center" | "right";
  noShadow?: boolean;
};

export default function Button({
  onClick,
  className,
  disabled = false,
  loading = false,
  type = "button",
  padding = "px-4 py-2",
  variant = "primary",
  style = {},
  icon,
  children,
  iconColor: iColor,
  color,
  textColor: tColor,
  align = "center",
  noShadow = false,
}: Props) {
  const { colors } = usePartnerContext();

  let textColor: string | undefined = colors?.primaryText;
  let mainColor: string | undefined = colors?.primary;
  let borderColor = "transparent";
  let iconColor = iColor || "primaryText";
  let boxShadowEnabled = true;
  let hoverBg =
    colors?.primaryHover || Color(colors?.primary).darken(0.1).hex();
  let activeBg =
    colors?.primaryActive || Color(colors?.primary).darken(0.2).hex();

  if (variant === "success") {
    mainColor = colors?.success;
    borderColor = "transparent";
    textColor = colors.successText;
    iconColor = "successText";
    hoverBg = Color(colors?.success).darken(0.1).hex();
    activeBg = Color(colors?.success).darken(0.15).hex();
  }
  if (variant === "danger") {
    mainColor = colors?.danger;
    borderColor = "transparent";
    textColor = colors.dangerText;
    iconColor = "dangerText";
    hoverBg = Color(colors?.danger).darken(0.1).hex();
    activeBg = Color(colors?.danger).darken(0.15).hex();
  }
  if (variant === "warning") {
    mainColor = colors?.warning;
    borderColor = "transparent";
    textColor = colors.warningText;
    iconColor = "warningText";
    hoverBg = Color(colors?.warning).darken(0.1).hex();
    activeBg = Color(colors?.warning).darken(0.15).hex();
  }
  if (variant === "info") {
    mainColor = colors?.info;
    borderColor = "transparent";
    textColor = colors?.infoText;
    iconColor = "infoText";
    hoverBg = Color(colors?.info).darken(0.1).hex();
    activeBg = Color(colors?.info).darken(0.15).hex();
  }
  if (variant === "info2") {
    mainColor = Color(colors?.info).darken(0.2).hex();
    borderColor = "transparent";
    textColor = Color(mainColor).isLight() ? "black" : "white";
    hoverBg = Color(mainColor).darken(0.1).hex();
    activeBg = Color(mainColor).darken(0.15).hex();
  }
  if (variant === "ghost") {
    mainColor = "transparent";
    borderColor = "transparent";
    textColor = colors?.textColor;
    iconColor = "textColor";
    boxShadowEnabled = false;
    hoverBg = colors?.surfaceHover || colors?.gray50;
    activeBg = colors?.surfaceActive || colors?.gray100;
  }
  if (variant === "gray") {
    mainColor = colors?.gray100 || colors?.gray2;
    borderColor = "transparent";
    textColor = colors?.textColor;
    iconColor = "textColor";
    hoverBg = colors?.gray200 || colors?.gray3;
    activeBg = colors?.gray300 || colors?.gray4;
  }

  if (noShadow) {
    boxShadowEnabled = false;
  }

  mainColor = color ? colors[color] : mainColor;
  textColor = tColor ? colors[tColor] : textColor;

  // Compute alignment class without nested ternary
  const getAlignmentClass = () => {
    if (align === "left") return "justify-start";
    if (align === "right") return "justify-end";
    return "justify-center";
  };
  const alignmentClass = getAlignmentClass();

  // Use React state for hover/active instead of direct DOM manipulation
  const [isHovered, setIsHovered] = useState(false);
  const [isActive, setIsActive] = useState(false);

  // Compute current background based on state (avoiding nested ternary)
  const getCurrentBg = () => {
    if (isActive) return activeBg;
    if (isHovered) return hoverBg;
    return mainColor;
  };
  const currentBg = getCurrentBg();

  // Compute shadow based on state (avoiding nested ternary)
  const getCurrentShadow = () => {
    if (!boxShadowEnabled || disabled) return undefined;
    if (isHovered)
      return "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)";
    return "0 1px 2px 0 rgba(0, 0, 0, 0.05)";
  };
  const currentShadow = getCurrentShadow();

  // Render button with explicit type to satisfy react/button-has-type rule
  const buttonProps = {
    onClick,
    className: `
      relative ${padding} rounded-md font-medium
      transition-all duration-150
      focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
      disabled:opacity-50 disabled:cursor-not-allowed
      ${className}
    `,
    style: {
      borderColor,
      backgroundColor: currentBg,
      color: textColor,
      boxShadow: currentShadow,
      borderWidth: borderColor !== "transparent" ? "1px" : "0",
      borderStyle: "solid",
      ...style,
    } as React.CSSProperties,
    disabled: disabled || loading,
    onMouseEnter: () => !disabled && !loading && setIsHovered(true),
    onMouseLeave: () => {
      setIsHovered(false);
      setIsActive(false);
    },
    onMouseDown: () => !disabled && !loading && setIsActive(true),
    onMouseUp: () => setIsActive(false),
  };

  const buttonContent = (
    <>
      {loading && (
        <div
          className={
            "absolute top-0 left-0 w-full h-full flex justify-center items-center rounded-md"
          }
        >
          <span className={"animate-pulse relative"}>
            <Loader />
          </span>
        </div>
      )}
      <div
        className={`flex ${alignmentClass} items-center`}
        style={{ opacity: loading ? 0 : 1 }}
      >
        {icon && (
          <UTIcon
            color={colors[iconColor]}
            type={icon}
            size={18}
            className={children ? "mr-2" : ""}
          />
        )}
        {children}
      </div>
    </>
  );

  // Use explicit type attributes to satisfy react/button-has-type
  if (type === "submit") {
    return (
      <button type={"submit"} {...buttonProps}>
        {buttonContent}
      </button>
    );
  }
  return (
    <button type={"button"} {...buttonProps}>
      {buttonContent}
    </button>
  );
}
